import React from 'react';
import { Check, HelpCircle } from 'lucide-react';
import { SmallText, BodyText } from './BrandTypography';

/**
 * GentleOptions Component
 * 
 * A trauma-informed, ADHD-friendly selection component that presents
 * choices in a soft, low-pressure way.
 * 
 * Features:
 * - Soft rounded card containers
 * - Wide spacing between options
 * - Warm, low-contrast backgrounds
 * - Gentle hover/active states
 * - Always includes "I'm not sure" option
 * - Never forces selection
 */

export const GentleOptions = ({
    options,
    selectedId,
    onSelect,
    allowMultiple = false,
    selectedIds = [],
    showUnsure = true,
    unsureLabel = "I'm not sure",
    className = ''
}) => {
    const isSelected = (id) => {
        if (allowMultiple) {
            return selectedIds.includes(id);
        }
        return selectedId === id;
    };

    const handleSelect = (id) => {
        onSelect(id);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {options.map((option, index) => (
                <GentleOptionCard
                    key={option.id}
                    option={option}
                    isSelected={isSelected(option.id)}
                    onSelect={() => handleSelect(option.id)}
                    index={index}
                />
            ))}
            
            {showUnsure && (
                <GentleOptionCard
                    option={{ id: 'unsure', label: unsureLabel }}
                    isSelected={isSelected('unsure')}
                    onSelect={() => handleSelect('unsure')}
                    index={options.length}
                    isUnsure
                />
            )}
        </div>
    );
};

export const GentleOptionCard = ({
    option,
    isSelected,
    onSelect,
    index = 0,
    isUnsure = false,
    className = ''
}) => {
    return (
        <button
            onClick={onSelect}
            className={`
                w-full p-5 sm:p-6 rounded-2xl text-left transition-all duration-200
                border-2 
                ${isSelected 
                    ? 'border-[#1D6B6B] bg-[#7D8E95]/10' 
                    : 'border-[#FFBB98] bg-[#FEF6EF] hover:bg-[#FBE0C3] hover:border-[#D9D2C9]'
                }
                brand-slide-up
            `}
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`gentle-option-${option.id}`}
        >
            <div className="flex items-center gap-4">
                {/* Selection indicator */}
                <div className={`
                    w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                    transition-colors duration-200
                    ${isSelected 
                        ? 'bg-[#1D6B6B]' 
                        : 'bg-[#FFBB98]'
                    }
                `}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                    {isUnsure && !isSelected && <HelpCircle className="w-3.5 h-3.5 text-[#7D8E95]" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <BodyText className={`font-medium text-lg ${isSelected ? 'text-[#1D6B6B]' : 'text-[#2A8585]'}`}>
                        {option.label}
                    </BodyText>
                    {option.description && (
                        <SmallText className="text-[#7D8E95] mt-1.5 leading-relaxed">
                            {option.description}
                        </SmallText>
                    )}
                </div>
            </div>
        </button>
    );
};

/**
 * GentleMultiSelect Component
 * 
 * For selecting multiple options with soft visual feedback.
 */
export const GentleMultiSelect = ({
    options,
    selectedIds = [],
    onToggle,
    showUnsure = true,
    unsureLabel = "I'm not sure",
    isUnsureSelected = false,
    onUnsureSelect,
    className = ''
}) => {
    const handleToggle = (id) => {
        onToggle(id);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {options.map((option, index) => {
                const isSelected = selectedIds.includes(option.id);
                return (
                    <GentleOptionCard
                        key={option.id}
                        option={option}
                        isSelected={isSelected}
                        onSelect={() => handleToggle(option.id)}
                        index={index}
                    />
                );
            })}
            
            {showUnsure && (
                <GentleOptionCard
                    option={{ id: 'unsure', label: unsureLabel }}
                    isSelected={isUnsureSelected}
                    onSelect={onUnsureSelect}
                    index={options.length}
                    isUnsure
                />
            )}
        </div>
    );
};

/**
 * GentleConfirmation Component
 * 
 * Soft confirmation with two gentle options - increased text sizes.
 */
export const GentleConfirmation = ({
    message,
    confirmLabel = "Continue",
    cancelLabel = "Go back",
    onConfirm,
    onCancel,
    className = ''
}) => {
    return (
        <div className={`space-y-5 ${className}`}>
            <BodyText className="text-[#2A8585] text-center text-lg leading-[1.7]">
                {message}
            </BodyText>
            <div className="space-y-3">
                <button
                    onClick={onConfirm}
                    className="w-full p-5 rounded-2xl bg-[#7D8E95] text-[#1D6B6B] text-lg font-medium
                               hover:bg-[#9AB896] transition-colors duration-200"
                    data-testid="gentle-confirm"
                >
                    {confirmLabel}
                </button>
                <button
                    onClick={onCancel}
                    className="w-full p-5 rounded-2xl bg-[#FFBB98] text-[#2A8585] text-lg font-medium
                               hover:bg-[#DDD6CA] transition-colors duration-200"
                    data-testid="gentle-cancel"
                >
                    {cancelLabel}
                </button>
            </div>
        </div>
    );
};

/**
 * GentleFooter Component
 * 
 * Standard footer with reassurance and navigation.
 */
export const GentleFooter = ({
    showOptional = true,
    showLeave = true,
    onLeave,
    customMessage,
    className = ''
}) => {
    return (
        <div className={`text-center space-y-3 ${className}`}>
            {showOptional && (
                <SmallText className="text-[#7D8E95]">
                    Everything here is optional.
                </SmallText>
            )}
            {customMessage && (
                <SmallText className="text-[#7D8E95]">
                    {customMessage}
                </SmallText>
            )}
            {showLeave && onLeave && (
                <button
                    onClick={onLeave}
                    className="text-[#FFBB98] hover:text-[#FFBB98] text-sm font-medium
                               transition-colors duration-200"
                    data-testid="gentle-leave"
                >
                    Come back later
                </button>
            )}
        </div>
    );
};

export default GentleOptions;
